<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  phoneNumber: '',
  server_uri: '',
  password: '',
  total_channels: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  phoneNumber: { required, isPhoneE164 },
  server_uri: { required },
  password: { required },
  total_channels: { required, minValue: minValue(1) },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  phoneNumber: v$.value.phoneNumber?.$error
    ? t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.ERROR')
    : '',
  server_uri: v$.value.server_uri?.$error
    ? t('INBOX_MGMT.ADD.VOICE.ACI.SERVER_URI.REQUIRED')
    : '',
  password: v$.value.password?.$error
    ? t('INBOX_MGMT.ADD.VOICE.ACI.PASSWORD.REQUIRED')
    : '',
  total_channels: v$.value.total_channels?.$error
    ? t('INBOX_MGMT.ADD.VOICE.ACI.TOTAL_CHANNELS.REQUIRED')
    : '',
}));

function getProviderConfig() {
  return {
    server_uri: state.server_uri,
    password: state.password,
    total_channels: parseInt(state.total_channels, 10),
  };
}

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const channel = await store.dispatch('inboxes/createVoiceChannel', {
      name: `Voice (${state.phoneNumber})`,
      voice: {
        phone_number: state.phoneNumber,
        provider: 'ACI',
        provider_config: getProviderConfig(),
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { inbox_id: channel.id },
    });
  } catch (error) {
    console.error('createChannel error:', error);
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.VOICE.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div
    class="overflow-auto col-span-6 p-6 w-full h-full rounded-t-lg border border-b-0 border-n-weak bg-n-solid-1"
  >
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VOICE.DESC')"
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.PLACEHOLDER')"
        :message="formErrors.phoneNumber"
        :message-type="formErrors.phoneNumber ? 'error' : 'info'"
        @blur="v$.phoneNumber?.$touch"
      />

      <Input
        v-model="state.server_uri"
        :label="t('INBOX_MGMT.ADD.VOICE.ACI.SERVER_URI.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.ACI.SERVER_URI.PLACEHOLDER')"
        :message="formErrors.server_uri"
        :message-type="formErrors.server_uri ? 'error' : 'info'"
        @blur="v$.server_uri?.$touch"
      />

      <Input
        v-model="state.password"
        type="password"
        :label="t('INBOX_MGMT.ADD.VOICE.ACI.PASSWORD.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.ACI.PASSWORD.PLACEHOLDER')"
        :message="formErrors.password"
        :message-type="formErrors.password ? 'error' : 'info'"
        @blur="v$.password?.$touch"
      />

      <!-- Total de Canais com explicação -->
      <div class="flex flex-col gap-1">
        <Input
          v-model="state.total_channels"
          type="number"
          min="1"
          :label="t('INBOX_MGMT.ADD.VOICE.ACI.TOTAL_CHANNELS.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.ACI.TOTAL_CHANNELS.PLACEHOLDER')"
          :message="formErrors.total_channels"
          :message-type="formErrors.total_channels ? 'error' : 'info'"
          @blur="v$.total_channels?.$touch"
        />
        
        <!-- Texto explicativo -->
        <div class="flex items-start gap-2 p-3 mt-1 rounded-lg bg-n-alpha-1 border border-n-weak">
          <svg 
            class="w-5 h-5 text-n-slate-11 flex-shrink-0 mt-0.5" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-width="2" 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
          <div class="text-sm text-n-slate-11">
            <p class="font-medium text-n-slate-12 mb-1">
              {{ t('INBOX_MGMT.ADD.VOICE.ACI.TOTAL_CHANNELS.INFO_TITLE') }}
            </p>
            <p>
              {{ t('INBOX_MGMT.ADD.VOICE.ACI.TOTAL_CHANNELS.INFO_DESC') }}
            </p>
            <p class="mt-2 text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.ADD.VOICE.ACI.TOTAL_CHANNELS.INFO_EXAMPLE') }}
            </p>
          </div>
        </div>
      </div>

      <div class="mt-2">
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VOICE.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>